const PAGE_WRAPPER_SELECTOR = '.page-wrapper';
const SQUEEZE_QUERY = window.matchMedia('(min-width: 990px)');

/**
 * Whether the theme is rendering inside the theme editor (design mode).
 *
 * In the editor the squeeze layout is intentionally disabled (see base.css) so the document root
 * scrolls natively and the editor's built-in "scroll to selected section" works without any
 * workarounds. The scroll-container logic below stays in sync by treating the squeeze layout as
 * inactive in design mode. Read from the `shopify-design-mode` class on `<html>` (set in
 * theme.liquid) so it is reliable at module-evaluation time.
 */
const IS_DESIGN_MODE = document.documentElement.classList.contains('shopify-design-mode');

/**
 * Whether the squeeze layout (≥990px, with `.page-wrapper` as the scroll container) is active.
 *
 * @returns {boolean}
 */
function isSqueezeActive() {
  return SQUEEZE_QUERY.matches && !IS_DESIGN_MODE;
}

/**
 * Returns the current page scroll container.
 * In the squeeze layout (desktop ≥990px), `.page-wrapper` is the scroll container instead of
 * `document.scrollingElement`. On mobile, the document root scrolls natively so the address bar
 * can hide/show.
 *
 * @returns {Element} The scroll container element
 */
function getScrollContainer() {
  if (isSqueezeActive()) {
    return document.querySelector(PAGE_WRAPPER_SELECTOR) ?? document.scrollingElement ?? document.documentElement;
  }
  return document.scrollingElement ?? document.documentElement;
}

/**
 * Returns the current scroll position of the page scroll container.
 *
 * @returns {number} The scrollTop value
 */
function getScrollTop() {
  return getScrollContainer().scrollTop;
}

/**
 * Scrolls the page scroll container to the specified position.
 *
 * @param {ScrollToOptions} options - The scroll options (top, left, behavior)
 */
function scrollTo(options) {
  getScrollContainer().scrollTo(options);
}

/**
 * Manual scroll restoration for `.page-wrapper` as scroll container.
 *
 * On desktop (≥990px), the browser restores scroll on `document.scrollingElement`, which
 * has `overflow: hidden` in the squeeze layout and can never scroll. We disable native
 * restoration and handle it via `history.state` instead.
 *
 * On mobile (<990px), `document.scrollingElement` IS the scroll container, so native
 * restoration works perfectly — leave it enabled to avoid a one-frame flash from rAF.
 *
 * Save: `pagehide` — fires exactly once per navigation, captures the exact scroll position
 * at the moment the user leaves. More accurate than debounced scroll (which can be stale
 * if the user scrolls and immediately clicks a link).
 *
 * Restore: unconditional `pageshow` — Horizon uses cross-document view transitions, so
 * `pageshow` fires on every navigation (both bfcache and fresh loads). `popstate` is not
 * used because it doesn't fire for cross-document back navigation.
 */
if (isSqueezeActive()) {
  history.scrollRestoration = 'manual';
}

/**
 * Transfers the scroll offset between scroll containers when the layout crosses the 990px
 * breakpoint (e.g. rotating a tablet between portrait and landscape).
 *
 * The squeeze layout (≥990px) makes `.page-wrapper` the scroll container and applies
 * `overflow: hidden` to `<html>`/`<body>`. If the user has scrolled the document root while on
 * mobile (<990px) and then crosses into the squeeze layout, the document root keeps its now-frozen
 * scrollTop — it can no longer scroll, so all content is pushed off-screen and the viewport shows a
 * blank background that the user cannot scroll back into view. This moves the offset onto whichever
 * container actually scrolls in the new layout and zeroes the one that no longer does.
 */
function syncScrollAcrossLayoutChange() {
  const pageWrapper = document.querySelector(PAGE_WRAPPER_SELECTOR);
  if (!pageWrapper) return;

  const root = document.scrollingElement ?? document.documentElement;

  // The scroll position can end up on either container as the breakpoint flips: the browser's
  // own scroll-anchoring sometimes moves it in time and sometimes leaves it stranded on the
  // element that is no longer the scroll container (which is why the glitch is intermittent).
  // Consolidate whichever offset is set onto the container that scrolls in the NEW layout, then
  // zero the other one so a frozen offset can't push the page off-screen behind a blank viewport.
  const offset = Math.max(root.scrollTop || 0, pageWrapper.scrollTop || 0);

  if (isSqueezeActive()) {
    // Squeeze layout (≥990px): `.page-wrapper` is the scroll container while the document root is
    // `overflow: hidden`. The root MUST sit at 0 — any frozen offset there hides the whole page
    // behind a blank viewport. Clear it unconditionally, then move the position onto `.page-wrapper`.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    if (offset > 0) pageWrapper.scrollTo({ top: offset, behavior: 'instant' });
  } else if (offset > 0) {
    // Mobile layout (<990px): the document root scrolls natively; `.page-wrapper` does not. Only
    // hand back an offset that is still present, so we don't clobber a position the browser may
    // have already restored onto the document root.
    pageWrapper.scrollTop = 0;
    window.scrollTo({ top: offset, left: 0, behavior: 'instant' });
  }
}

SQUEEZE_QUERY.addEventListener('change', () => {
  history.scrollRestoration = isSqueezeActive() ? 'manual' : 'auto';
  syncScrollAcrossLayoutChange();
});

/**
 * Saves the current scroll position into the current history entry.
 */
function saveScrollPosition() {
  try {
    const currentState = typeof history.state === 'object' && history.state !== null ? history.state : {};
    history.replaceState({ ...currentState, scrollTop: getScrollContainer().scrollTop }, '');
  } catch (_) {
    // replaceState can throw if the state object exceeds the browser's size limit
  }
}

window.addEventListener('pagehide', saveScrollPosition);

/**
 * Restores a saved scroll position onto the current scroll container.
 *
 * @param {number} savedScrollTop
 */
function restoreSavedScrollTop(savedScrollTop) {
  if (!Number.isFinite(savedScrollTop) || savedScrollTop < 0) return;

  const container = getScrollContainer();
  const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
  const targetScrollTop = Math.min(savedScrollTop, maxScrollTop);

  // Use scrollTo with 'instant' to override CSS scroll-behavior: smooth on .page-wrapper
  container.scrollTo({ top: targetScrollTop, behavior: 'instant' });
}

window.addEventListener('pageshow', () => {
  const scrollTop = history.state?.scrollTop;
  if (scrollTop != null) {
    requestAnimationFrame(() => {
      restoreSavedScrollTop(scrollTop);
    });
    return;
  }

  if (window.location.hash) {
    requestAnimationFrame(() => scrollToCurrentHash('instant'));
  }
});

/**
 * Same-document scroll restoration for pushState navigation.
 *
 * `pagehide`/`pageshow` only fire for cross-document navigations. For same-document
 * pushState navigation (filters, pagination), we patch pushState to auto-save scroll
 * position before each new entry, and restore on popstate (back/forward).
 */
const originalPushState = history.pushState.bind(history);
history.pushState = function (state, title, url) {
  saveScrollPosition();
  originalPushState(state, title, url);
};

window.addEventListener('popstate', () => {
  if (window.location.hash && scrollToHashTarget(window.location.hash, 'instant')) {
    return;
  }

  const scrollTop = history.state?.scrollTop;
  if (scrollTop == null) return;

  requestAnimationFrame(() => {
    restoreSavedScrollTop(scrollTop);
  });
});

/**
 * Returns the appropriate target for listening to scroll events.
 * On desktop (≥990px), `.page-wrapper` emits scroll events directly.
 * On mobile, the document root scrolls natively and scroll events bubble to `document`.
 *
 * @returns {EventTarget} The target to call addEventListener('scroll', ...) on
 */
function getScrollEventTarget() {
  if (isSqueezeActive()) {
    return document.querySelector(PAGE_WRAPPER_SELECTOR) ?? document;
  }
  return document;
}

/**
 * Returns the appropriate root for an IntersectionObserver monitoring the scroll container.
 * On desktop (≥990px), `.page-wrapper` must be set as the explicit root.
 * On mobile, `null` uses the viewport root (the IntersectionObserver default).
 *
 * @returns {Element | null} The root option for IntersectionObserver
 */
function getIntersectionRoot() {
  if (isSqueezeActive()) {
    return document.querySelector(PAGE_WRAPPER_SELECTOR) ?? null;
  }
  return null;
}

/**
 * Offset for sticky header when scrolling to in-page anchors.
 *
 * @returns {number}
 */
function getHeaderScrollOffset() {
  const styles = getComputedStyle(document.body);
  const groupHeight = parseFloat(styles.getPropertyValue('--header-group-height'));
  if (Number.isFinite(groupHeight) && groupHeight > 0) return groupHeight;

  const headerHeight = parseFloat(styles.getPropertyValue('--header-height'));
  if (Number.isFinite(headerHeight) && headerHeight > 0) return headerHeight;

  return 0;
}

/**
 * Scrolls the page scroll container to an in-page hash target.
 * Required because desktop (≥990px) scrolls `.page-wrapper`, not the document root.
 *
 * @param {string} hash
 * @param {ScrollBehavior} [behavior='smooth']
 * @returns {boolean}
 */
function scrollToHashTarget(hash, behavior = 'smooth') {
  if (!hash || hash === '#') return false;

  const id = decodeURIComponent(hash.slice(1));
  if (!id) return false;

  const target = document.getElementById(id);
  if (!target) return false;

  const container = getScrollContainer();
  const offset = getHeaderScrollOffset() + 12;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollBehavior = behavior === 'smooth' && prefersReducedMotion ? 'instant' : behavior;

  let targetScrollTop;

  if (container === document.scrollingElement || container === document.documentElement) {
    targetScrollTop = target.getBoundingClientRect().top + window.scrollY - offset;
  } else {
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    targetScrollTop = container.scrollTop + (targetRect.top - containerRect.top) - offset;
  }

  const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
  container.scrollTo({
    top: Math.min(maxScrollTop, Math.max(0, targetScrollTop)),
    behavior: scrollBehavior,
  });

  return true;
}

/**
 * @param {MouseEvent} event
 */
function handleAnchorLinkClick(event) {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return;
  }

  const anchor = event.target instanceof Element ? event.target.closest('a[href^="#"]') : null;
  if (!(anchor instanceof HTMLAnchorElement)) return;

  const href = anchor.getAttribute('href');
  if (!href || href === '#') return;

  const url = new URL(anchor.href);
  if (url.origin !== window.location.origin) return;
  if (url.pathname !== window.location.pathname) return;
  if (url.search !== window.location.search) return;
  if (!url.hash) return;

  if (!scrollToHashTarget(url.hash)) return;

  event.preventDefault();
  history.pushState(null, '', url.hash);
}

document.addEventListener('click', handleAnchorLinkClick, true);

function scrollToCurrentHash(behavior = 'instant') {
  if (!window.location.hash) return;
  scrollToHashTarget(window.location.hash, behavior);
}

window.addEventListener('DOMContentLoaded', () => {
  requestAnimationFrame(() => scrollToCurrentHash('instant'));
});

export {
  getScrollContainer,
  getScrollTop,
  scrollTo,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollToHashTarget,
  SQUEEZE_QUERY as scrollContainerMediaQuery,
};
